'use client';

import { useState, useCallback } from 'react';
import type { GridApi } from 'ag-grid-community';
import { ViewIcon, Settings01Icon } from '@terra/ui-icons';
import { HStack, Box } from '@terra/ui-styled-system';
import { IconButton } from '../icon-button';
import { Popover } from '../popover';
import { Checkbox } from '../checkbox';
import { Text } from '../text';
import { Button } from '../button';
import { Tooltip } from './tooltip';
import { useTranslations } from 'next-intl';

export type ColumnInfo = {
  colId: string;
  headerName: string;
  visible: boolean;
};
type ColumnVisibilityPanelProps = {
  gridApi?: GridApi | null;
  columns: ColumnInfo[];
  onColumnVisibilityChange: (colId: string, visible: boolean) => void;
  onBulkColumnVisibilityChange: (
    columnUpdates: {
      colId: string;
      visible: boolean;
    }[],
  ) => void;
};

export function ColumnVisibilityPanel({
  gridApi,
  columns,
  onColumnVisibilityChange,
  onBulkColumnVisibilityChange,
}: ColumnVisibilityPanelProps) {
  const t = useTranslations('designSystem.table.columnVisibility');
  const [isOpen, setIsOpen] = useState(false);
  const handleColumnToggle = useCallback(
    (colId: string, visible: boolean) => {
      if (gridApi) {
        gridApi.setColumnsVisible([colId], visible);
      }

      onColumnVisibilityChange(colId, visible);
    },
    [gridApi, onColumnVisibilityChange],
  );
  const handleShowAll = useCallback(() => {
    if (!columns) return;
    const columnIds = columns.map((col) => col.colId);

    if (gridApi) {
      gridApi.setColumnsVisible(columnIds, true);
    }

    const columnUpdates = columns.map((col) => ({
      colId: col.colId,
      visible: true,
    }));

    onBulkColumnVisibilityChange(columnUpdates);
  }, [columns, gridApi, onBulkColumnVisibilityChange]);

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      positioning={{
        placement: 'bottom-end',
      }}
    >
      {!isOpen ? (
        <Tooltip title={t('tooltip')}>
          <Popover.Trigger asChild>
            <IconButton variant="ghost" size="sm" aria-label={t('togglePanel')}>
              <Settings01Icon size={16} />
            </IconButton>
          </Popover.Trigger>
        </Tooltip>
      ) : (
        <Popover.Trigger asChild>
          <IconButton variant="ghost" size="sm" aria-label={t('togglePanel')}>
            <Settings01Icon size={16} />
          </IconButton>
        </Popover.Trigger>
      )}

      <Popover.Positioner>
        <Popover.Content>
          <Popover.Arrow>
            <Popover.ArrowTip />
          </Popover.Arrow>

          <Box p={4} minW="250px" maxW="300px">
            <HStack justifyContent="space-between" w="full" mb={4}>
              <Text fontWeight="semibold">{t('title')}</Text>
              <Button variant="outline" size="xs" onClick={handleShowAll}>
                <ViewIcon size={14} />
                {t('showAll')}
              </Button>
            </HStack>

            <Box maxH="300px" overflowY="auto">
              {columns?.map((column) => (
                <HStack
                  key={column.colId}
                  w="full"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Checkbox
                    checked={column.visible}
                    onCheckedChange={({ checked }) => {
                      handleColumnToggle(column.colId, checked as boolean);
                    }}
                    size="sm"
                  >
                    <Text fontSize="sm" truncate maxW="180px">
                      {column.headerName || column.colId}
                    </Text>
                  </Checkbox>
                </HStack>
              ))}
            </Box>
          </Box>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
